import {
  parseArray,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import { createUrl, IHttpResponse, restGet, restPatch, restPost } from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';

// BRAND SAFETY TRACKER

export interface IBrandSafetyTracker {
  CreatedAt?: string;
  ID: number;
  PublisherID: number; // id of the publisher that owns this tracker
  Name: string; // rule name
  Active: BrandSafetyTrackerActive; // is rule active - BrandSafetyRuleActive enum
  ThirdParty: BrandSafetyThirdParty; // third party options (liveramp, doubleverify)
  AlwaysOn: BrandSafetyTrackerAlwaysOn; // determines if start/end date are ignored
  StartDate: string;
  EndDate: string;
  Timezone: string;
  MeasuredInventory: number; // percentage of inventory where tracker event pixel should be applied (0-100)
  BrandSafetyTrackerEvents: IBrandSafetyTrackerEvent[];
  SiteIDS: number[];
  ChannelGroupIDS: number[];
  BidderIDS: number[];
  BidderGroupIDS: number[];
  ChannelTargeting: BrandSafetyTrackerChannelTargeting;
}

export type BrandSafetyTrackerActive = number;
export const BrandSafetyTrackerActiveUnknown: BrandSafetyTrackerActive = 0;
export const BrandSafetyTrackerActiveInactive: BrandSafetyTrackerActive = 1;
export const BrandSafetyTrackerActiveActive: BrandSafetyTrackerActive = 2;

export type BrandSafetyTrackerChannelTargeting = number;
export const BrandSafetyTrackerChannelTargetingUnknown: BrandSafetyTrackerChannelTargeting = 0;
export const BrandSafetyTrackerChannelTargetingAll: BrandSafetyTrackerChannelTargeting = 1;
export const BrandSafetyTrackerChannelTargetingSelected: BrandSafetyTrackerChannelTargeting = 2;
export const BrandSafetyTrackerBidderTargetingAll: BrandSafetyTrackerChannelTargeting = 3;
export const BrandSafetyTrackerBidderTargetingSelected: BrandSafetyTrackerChannelTargeting = 4;

export type BrandSafetyThirdParty = number;
export const BrandSafetyThirdPartyUnknown: BrandSafetyThirdParty = 0;
export const BrandSafetyThirdPartyIAS: BrandSafetyThirdParty = 1;
export const BrandSafetyThirdPartyProtectedMedia: BrandSafetyThirdParty = 2;
export const BrandSafetyThirdPartyMoat: BrandSafetyThirdParty = 3;
export const BrandSafetyThirdPartyDoubleVerify: BrandSafetyThirdParty = 4;
export const BrandSafetyThirdPartyOther: BrandSafetyThirdParty = 5;
export const BrandSafetyThirdPartyPixalate: BrandSafetyThirdParty = 6;

export type BrandSafetyTrackerAlwaysOn = number;
export const BrandSafetyTrackerAlwaysOnUnknown: BrandSafetyTrackerAlwaysOn = 0;
export const BrandSafetyTrackerAlwaysOnInactive: BrandSafetyTrackerAlwaysOn = 1;
export const BrandSafetyTrackerAlwaysOnActive: BrandSafetyTrackerAlwaysOn = 2;

// BRAND SAFETY TRACKER EVENT
export interface IBrandSafetyTrackerEvent {
  ID: number;
  BrandSafetyTrackerID: number;
  Active: BrandSafetyTrackerActive;
  EventType: BrandSafetyTrackerEventType;
  PixelURL: string;
  FireOncePerPod: EBool;
}

export type BrandSafetyTrackerEventActive = number;
export const BrandSafetyTrackerEventActiveUnknown: BrandSafetyTrackerEventActive = 0;
export const BrandSafetyTrackerEventActiveInactive: BrandSafetyTrackerEventActive = 1;
export const BrandSafetyTrackerEventActiveActive: BrandSafetyTrackerEventActive = 2;

export type BrandSafetyTrackerEventType = number;
export const BrandSafetyTrackerEventTypeUnknown: BrandSafetyTrackerEventType = 0;
export const BrandSafetyTrackerEventTypeImpression: BrandSafetyTrackerEventType = 1;
export const BrandSafetyTrackerEventTypeStart: BrandSafetyTrackerEventType = 2;
export const BrandSafetyTrackerEventTypeQuartile1: BrandSafetyTrackerEventType = 3;
export const BrandSafetyTrackerEventTypeQuartile2: BrandSafetyTrackerEventType = 4;
export const BrandSafetyTrackerEventTypeQuartile3: BrandSafetyTrackerEventType = 5;
export const BrandSafetyTrackerEventTypeComplete: BrandSafetyTrackerEventType = 6;

export const transformBrandSafetyTrackerEvent = transformModel<IBrandSafetyTrackerEvent>({
  ID: parseNumber(['ID', 'id']),
  BrandSafetyTrackerID: parseNumber(['BrandSafetyTrackerID', 'brand_safety_tracker_id']),
  Active: parseNumber(['Active', 'active']),
  EventType: parseNumber(['EventType', 'event_type']),
  PixelURL: parseString(['PixelURL', 'pixel_url']),
  FireOncePerPod: parseNumber(['FireOncePerPod', 'fire_once_per_pod']),
});

export const transformBrandSafetyTracker = transformModel<IBrandSafetyTracker>({
  CreatedAt: 'CreatedAt',
  ID: parseNumber(['ID', 'id']),
  PublisherID: parseNumber(['PublisherID', 'publisher_id']),
  Name: parseString(['Name', 'name']),
  Active: parseNumber(['Active', 'active']),
  ThirdParty: parseNumber(['ThirdParty', 'third_party']),
  AlwaysOn: parseNumber(['AlwaysOn', 'always_on']),
  StartDate: parseString(['StartDate', 'start_date']),
  EndDate: parseString(['EndDate', 'end_date']),
  Timezone: parseString(['Timezone', 'timezone']),
  MeasuredInventory: parseNumber(['MeasuredInventory', 'measured_inventory']),
  ChannelTargeting: parseNumber(['ChannelTargeting', 'channel_targeting']),
  BrandSafetyTrackerEvents: parseArray(
    ['BrandSafetyTrackerEvents', 'brand_safety_tracker_events'],
    transformBrandSafetyTrackerEvent,
  ),
  SiteIDS: parseArray(['SiteIDS', 'site_ids'], (obj: any) => {
    return obj;
  }),
  ChannelGroupIDS: parseArray(['ChannelGroupIDS', 'channel_group_ids'], (obj: any) => {
    return obj;
  }),
  BidderIDS: parseArray(['BidderIDS', 'bidder_ids'], (obj: any) => {
    return obj;
  }),
  BidderGroupIDS: parseArray(['BidderGroupIDS', 'bidder_group_ids'], (obj: any) => {
    return obj;
  }),
});

// Brand Safety Tracker CRUD operations

export async function getBrandSafetyTrackers(
  publisherID: string,
): Promise<IHttpResponse<IBrandSafetyTracker[]>> {
  const reqUrl = createUrl(`/brand_safety_tracker`, publisherID);
  return await restGet<IBrandSafetyTracker[]>(reqUrl, {});
}

export async function getBrandSafetyTracker(
  brandSafetyTrackerID: string,
  publisherID: string,
): Promise<IHttpResponse<IBrandSafetyTracker>> {
  const reqUrl = createUrl(`/brand_safety_tracker/${brandSafetyTrackerID}`, publisherID);
  return await restGet<IBrandSafetyTracker>(reqUrl, {});
}

export async function createBrandSafetyTracker(
  brandSafetyTracker: IBrandSafetyTracker,
): Promise<IHttpResponse<IBrandSafetyTracker>> {
  const reqUrl = createUrl('/brand_safety_tracker', brandSafetyTracker.PublisherID.toString());
  const parsedBST = transformBrandSafetyTracker(brandSafetyTracker);
  return await restPost<IBrandSafetyTracker>(reqUrl, parsedBST);
}

export async function updateBrandSafetyTracker(
  brandSafetyTracker: IBrandSafetyTracker,
): Promise<IHttpResponse<IBrandSafetyTracker>> {
  const reqUrl = createUrl('/brand_safety_tracker', brandSafetyTracker.PublisherID.toString());
  const parsedBST = transformBrandSafetyTracker(brandSafetyTracker);
  return await restPatch<IBrandSafetyTracker>(reqUrl, parsedBST);
}

export function getTrackerEventTargeting(tracker: IBrandSafetyTracker): string {
  const eventNames = tracker.BrandSafetyTrackerEvents.map((event: IBrandSafetyTrackerEvent) => {
    switch (event.EventType) {
      case BrandSafetyTrackerEventTypeImpression:
        return 'Impression';
        break;
      case BrandSafetyTrackerEventTypeStart:
        return 'Start';
        break;
      case BrandSafetyTrackerEventTypeQuartile1:
        return 'Quartile 1';
        break;
      case BrandSafetyTrackerEventTypeQuartile2:
        return 'Quartile 2';
        break;
      case BrandSafetyTrackerEventTypeQuartile3:
        return 'Quartile 3';
        break;
      case BrandSafetyTrackerEventTypeComplete:
        return 'Complete';
        break;
      default:
        return 'Unknown';
    }
  });

  if (eventNames.length > 3) {
    return `${eventNames.length} event types`;
  } else {
    return eventNames.join(', ');
  }
}

export function getTrackerThirdParty(tracker: IBrandSafetyTracker): string {
  switch (tracker.ThirdParty) {
    case BrandSafetyThirdPartyIAS:
      return 'IAS';
    case BrandSafetyThirdPartyProtectedMedia:
      return 'Protected Media';
    case BrandSafetyThirdPartyMoat:
      return 'Moat';
    case BrandSafetyThirdPartyDoubleVerify:
      return 'Double Verify';
    case BrandSafetyThirdPartyPixalate:
      return 'Pixalate';
    case BrandSafetyThirdPartyOther:
      return 'Other';
    default:
      return 'Unknown';
  }
}
